/**
 * Present a risk alert as something a person can read.
 *
 * The backend bakes one string for every alert (AnalysisPubSubService):
 *
 *   `${SEVERITY.toUpperCase()} risk ${category} detected: ${reason}`
 *
 * so the raw taxonomy key reaches the user as snake_case ("hate_speech"), the
 * severity is shouted even though the UI already shows it as a coloured dot and
 * a badge, and the whole sentence is one unbroken line. Because that string is
 * persisted on the alert, reformatting it at the source would not fix the
 * records that already exist - so the presentation is fixed here, at render.
 *
 * The prefix is stripped only when it matches the known shape exactly.
 * Anything else is passed through untouched, so an alert written by some other
 * producer is never silently mangled.
 */

const PREFIX = /^\s*(critical|high|medium|low)\s+risk\s+(.+?)\s+detected:\s*/i;

/** "hate_speech" -> "Hate speech"; "political" -> "Political". */
export function humanizeCategory(category) {
	const s = String(category || '').trim().replace(/[_-]+/g, ' ');
	if (!s) return '';
	return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * @returns {{ category: string, detail: string }}
 *   `category` is a short human label ("Hate speech"), '' when unknown.
 *   `detail` is the finding itself, with the redundant prefix removed.
 */
export function formatAlert(alert) {
	const message = String(alert?.message || '').trim();
	const match = message.match(PREFIX);

	if (match) {
		return {
			category: humanizeCategory(match[2]),
			detail: message.slice(match[0].length).trim() || humanizeCategory(match[2]),
		};
	}

	// No recognisable prefix: fall back to the alert's own fields.
	return {
		category: humanizeCategory(alert?.category),
		detail: message || humanizeCategory(alert?.category) || 'High-risk content detected',
	};
}
